import type { CityPracticeConfig } from '../config';

/**
 * Default audio mapping for city-distance modes — ported from the prototype's
 * miles-based curves but in km internally (haversine returns km natively).
 *
 *   travelDuration: sqrt(km) scaled, 0.5s pinpoint → ~2s for >5000 km
 *   ding pitch:     <50 km → C major chord (great-guess feel)
 *                   else exponential interpolation 880 → 300 Hz over 0–6500 km
 */
function travelDurationMsForKm(km: number): number {
  const minMs = 500;
  const maxMs = 2000;
  const ref = Math.sqrt(8000); // km at which we hit maxMs
  const t = Math.min(1, Math.sqrt(Math.max(0, km)) / ref);
  return minMs + (maxMs - minMs) * t;
}

function dingFrequenciesForKm(km: number): number | number[] {
  if (km < 80) {
    // Great-guess C major chord (C5, E5, G5).
    return [523.25, 659.25, 783.99];
  }
  const lo = 300;
  const hi = 880;
  const refKm = 6500; // distance at which we hit the low end
  const t = Math.min(1, km / refKm);
  return hi * Math.pow(lo / hi, t);
}

/**
 * The flagship Practice mode — endless loop through tier-2 (~500) cities
 * worldwide. The first concrete game mode the product ships with.
 */
export const citiesWorldPractice: CityPracticeConfig = {
  id: 'cities-world-practice',
  scope: { entity: 'cities', region: 'world' },
  format: 'practice',
  interaction: 'click-distance',

  displayName: 'World Cities - Practice',
  shortName: 'World Cities',

  // Photo-only globe during the prompt — cleanest backdrop for a "find
  // the place on the map" guess. Borders mounted invisible (opacity 0)
  // so the mode component can fade them in after each guess as a
  // "see how close you were to the right country" overlay.
  layers: {
    texture: { visible: true, opacity: 1.0 },
    borders: { visible: true, opacity: 0 },
    fills: { visible: false, opacity: 0.55 },
    hover: false,
  },
  // User may opt borders/fills/hover on later via settings; texture is the
  // mode's defining surface so we don't allow turning it off.
  userMayToggle: {
    texture: false,
    borders: true,
    fills: true,
    hover: true,
  },

  unitSystem: 'miles',
  tierMax: 2,
  cityFilter: {},
  userMayChangeFilter: true,
  promptFormat: 'city-country',
  audio: { travelDurationMsForKm, dingFrequenciesForKm },
};
